export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  firmId: number | null;
  firmRoleId?: number | null;
}

export interface LoginResponse {
  token: string;
  user: User;
}

// Admin panel uses simpler login response
export interface AdminLoginResponse {
  token: string;
  user: User;
}

export type AgentBackend = 0 | 1; // 0 = RaporGorAgent, 1 = IttihatAgent

export const AGENT_BACKEND = {
  RaporGorAgent: 0,
  IttihatAgent: 1,
} as const;

export const AGENT_BACKEND_LABELS: Record<AgentBackend, string> = {
  0: 'RaporGor Agent',
  1: 'İttihat Hub',
};

export interface Firm {
  id: number;
  name: string;
  subdomain: string;
  schemaName: string;
  contactEmail: string | null;
  licenseKey: string;
  isActive: boolean;
  createdAt: string;
  // New fields
  dealerId: number | null;
  dealerName: string | null;
  licenseExpiresAt: string | null;
  licenseStatus: string;
  deactivationReason: string | null;
  deactivatedAt: string | null;
  officialName: string | null;
  address: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
  phone: string | null;
  city: string | null;
  district: string | null;
  postalCode: string | null;
  notes: string | null;
  agentBackend: AgentBackend;
  ittihatTenantId?: string | null;
  erpType?: string;
}

export interface CreateFirmRequest {
  name: string;
  subdomain: string;
  contactEmail?: string;
  dealerId?: number;
  licenseExpiresAt?: string;
  officialName?: string;
  address?: string;
  taxNumber?: string;
  taxOffice?: string;
  phone?: string;
  city?: string;
  district?: string;
  postalCode?: string;
  notes?: string;
}

export interface UpdateFirmRequest {
  name: string;
  contactEmail?: string;
  isActive: boolean;
  dealerId?: number;
  officialName?: string;
  address?: string;
  taxNumber?: string;
  taxOffice?: string;
  phone?: string;
  city?: string;
  district?: string;
  postalCode?: string;
  notes?: string;
  agentBackend?: AgentBackend;
  ittihatTenantId?: string | null;
  erpType?: string;
}

export interface Agent {
  id: number;
  firmId: number;
  name: string;
  apiKey: string;
  lastSeenAt: string | null;
  isOnline: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface DataSource {
  id: number;
  firmId: number;
  name: string;
  sourceType: string;
  sourceName: string;
  parameters: string | null;
  schedule: string | null;
  isActive: boolean;
  lastSyncAt: string | null;
}

export interface AgentVersion {
  id: number;
  version: string;
  downloadUrl: string;
  releaseNotes: string | null;
  checksum: string | null;
  fileSize: number;
  isMandatory: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface CreateVersionRequest {
  version: string;
  downloadUrl: string;
  releaseNotes?: string;
  checksum?: string;
  fileSize: number;
  isMandatory: boolean;
  notifyAgents?: boolean;
}

export interface UploadResponse {
  downloadUrl: string;
  checksum: string;
  fileSize: number;
}

// Panel User types
export interface PanelUser {
  id: number;
  email: string;
  name: string;
  role: string;
  firmId: number | null;
  firmName: string | null;
  firmRoleId: number | null;
  firmRoleName: string | null;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  /** E-posta adresinin doğrulandığı an; doğrulanmamışsa null. */
  emailVerifiedAt?: string | null;
  /** Yalnız parolasız (davet) açılışta true/false; diğer yanıtlarda null. */
  davetGonderildi?: boolean | null;
}

export interface CreateUserRequest {
  email: string;
  /** Boş bırakılırsa API kullanıcıyı davet e-postasıyla açar. */
  password?: string;
  name: string;
  role?: string;
  firmId?: number;
  firmRoleId?: number;
}

export interface UpdateUserRequest {
  email?: string;
  name?: string;
  role?: string;
  isActive?: boolean;
  firmId?: number;
  firmRoleId?: number;
}

// Dealer types
export interface Dealer {
  id: number;
  name: string;
  contactName: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
  city: string | null;
  district: string | null;
  commissionRate: number;
  notes: string | null;
  isActive: boolean;
  firmCount: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface CreateDealerRequest {
  name: string;
  contactName?: string;
  email: string;
  phone?: string;
  address?: string;
  taxNumber?: string;
  taxOffice?: string;
  city?: string;
  district?: string;
  commissionRate: number;
  notes?: string;
}

export interface UpdateDealerRequest extends CreateDealerRequest {
  isActive: boolean;
}

export interface DealerFirm {
  id: number;
  name: string;
  subdomain: string;
  contactEmail: string | null;
  licenseStatus: string;
  licenseExpiresAt: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface DealerStats {
  totalFirms: number;
  activeFirms: number;
  pausedFirms: number;
  expiredFirms: number;
  deactivatedFirms: number;
  expiringIn30Days: number;
}

// Announcement types
export interface Announcement {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  startsAt: string;
  endsAt: string;
  isGlobal: boolean;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  firmIds: number[];
  firms: { id: number; name: string }[];
}

export interface CreateAnnouncementRequest {
  title: string;
  message: string;
  type: string;
  startsAt: string;
  endsAt: string;
  isGlobal: boolean;
  firmIds?: number[];
}

export interface UpdateAnnouncementRequest extends CreateAnnouncementRequest {
  isActive: boolean;
}

export interface RolePermissions {
  allReports: boolean;
  sourceIds: number[];
}

export interface UserPermissions {
  allow: number[];
  deny: number[];
}

// Firm Role types (simplified - no permissions, managed in customer panel config)
export interface FirmRole {
  id: number;
  firmId: number;
  name: string;
  description: string | null;
  isDefault: boolean;
  userCount: number;
  createdAt: string;
}

export interface CreateFirmRoleRequest {
  name: string;
  description?: string;
  isDefault: boolean;
}

export interface UpdateFirmRoleRequest {
  name: string;
  description?: string;
  isDefault: boolean;
}

// Firm History types
export interface FirmHistory {
  id: number;
  actionType: string;
  description: string | null;
  oldValue: string | null;
  newValue: string | null;
  performedBy: string | null;
  createdAt: string;
}

// Firm Financial types
export interface FirmFinancial {
  id: number;
  transactionType: string;
  description: string;
  amount: number;
  currency: string;
  periodStart: string | null;
  periodEnd: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface CreateFinancialRequest {
  transactionType: string;
  description: string;
  amount: number;
  currency?: string;
  periodStart?: string;
  periodEnd?: string;
  notes?: string;
}

export interface FinancialSummary {
  totalAnnualFees: number;
  totalAddons: number;
  totalDiscounts: number;
  totalPayments: number;
  totalRevenue: number;
  transactionCount: number;
}

export type EnrollmentStatus = 'pending' | 'registered' | 'revoked' | 'expired';

export interface Enrollment {
  id: number;
  erpType: string;
  agentId: string | null;
  active: boolean;
  expiresAt: string | null;
  createdAt: string;
  status: EnrollmentStatus;
}

export interface CreateEnrollmentResult {
  id: number;
  token: string;
  installerUrl: string;
  expiresAt: string | null;
  status: EnrollmentStatus;
}
